<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="bg-primary-light p-3 rounded-2 me-3">
              <vue-feather type="bar-chart-2" stroke="#652aa3" size="22"></vue-feather>
            </div>
            <div>
              <h2 class="page-title mb-0">{{ cycle?.name }} Cycle Analytics</h2>
              <p class="text-muted mb-0" v-if="cycle">{{ cycle.name }} • {{ formatDate(cycle.start_date) }} - {{ formatDate(cycle.end_date) }}</p>
            </div>
          </div>
          <div>
            <button type="button" class="btn btn-outline-dark" @click="goBack">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else>

      <!-- Overview Metrics -->
      <div class="row mb-4">
        <div class="col-md-3 mb-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="text-muted small">Total Employees</span>
                <vue-feather type="users" size="20" class="text-primary"></vue-feather>
              </div>
              <h2 class="mb-0">{{ overview.total_employees || 0 }}</h2>
              <small class="text-success">{{ overview.completed_reviews || 0 }} completed</small>
            </div>
          </div>
        </div>

        <div class="col-md-3 mb-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="text-muted small">Average Score</span>
                <vue-feather type="award" size="20" class="text-warning"></vue-feather>
              </div>
              <h2 class="mb-0">{{ overview.average_score || 0 }}%</h2>
              <small :class="trends.trend_direction === 'up' ? 'text-success' : trends.trend_direction === 'down' ? 'text-danger' : 'text-muted'">
                <vue-feather :type="getTrendIcon()" size="14"></vue-feather>
                {{ trends.trend_direction }}
              </small>
            </div>
          </div>
        </div>

        <div class="col-md-3 mb-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="text-muted small">Completion Rate</span>
                <vue-feather type="check-circle" size="20" class="text-success"></vue-feather>
              </div>
              <h2 class="mb-0">{{ overview.completion_rate || 0 }}%</h2>
              <div class="progress" style="height: 4px">
                <div class="progress-bar bg-success" :style="`width: ${overview.completion_rate || 0}%`"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-3 mb-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="text-muted small">Days Remaining</span>
                <vue-feather type="calendar" size="20" class="text-info"></vue-feather>
              </div>
              <h2 class="mb-0">{{ progress.days_remaining ?? 'N/A' }}</h2>
              <small class="text-muted">{{ progress.cycle_progress || 0 }}% complete</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Rating Distribution Card -->
      <div class="row mb-4">
        <div class="col-md-4 mb-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">Rating Distribution</h5>
            </div>
            <div class="card-body">
              <div v-if="distribution && Object.keys(distribution).length">
                <div v-for="(count, rating) in distribution" :key="rating" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span class="text-capitalize">{{ formatRating(rating) }}</span>
                    <strong>{{ count }}</strong>
                  </div>
                  <div class="progress" style="height: 8px">
                    <div class="progress-bar" :class="getRatingColor(rating)" :style="`width: ${(count / getTotalRatings()) * 100}%`"></div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <vue-feather type="bar-chart" size="48" class="mb-2 opacity-50"></vue-feather>
                <p class="mb-0">No ratings yet</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Top Performers -->
        <div class="col-md-8 mb-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">Top Performers</h5>
            </div>
            <div class="card-body">
              <div v-if="topPerformers && topPerformers.length">
                <div v-for="(performer, index) in topPerformers" :key="performer.employee_id" class="d-flex align-items-center p-3 mb-2 rounded bg-light">
                  <div class="me-3">
                    <div class="badge rounded-circle" :class="getPerformerBadge(index)" style="width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 1rem">
                      {{ index + 1 }}
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0">{{ performer.employee_name }}</h6>
                    <small class="text-muted">{{ performer.department }} • {{ performer.designation }}</small>
                  </div>
                  <div class="text-end">
                    <div class="fw-bold text-primary mb-1">{{ parseFloat(performer.score).toFixed(0) }}%</div>
                    <span class="badge" :class="getRatingBadge(performer.rating)">{{ formatRating(performer.rating) }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <vue-feather type="award" size="48" class="mb-2 opacity-50"></vue-feather>
                <p class="mb-0">No top performers yet</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Department Stats Table -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom">
              <h5 class="mb-0">Department Performance</h5>
            </div>
            <div class="card-body">
              <div v-if="departmentStats && departmentStats.length" class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th>Total Employees</th>
                      <th>Reviews Completed</th>
                      <th>Completion Rate</th>
                      <th>Average Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dept in departmentStats" :key="dept.department_id">
                      <td>
                        <div class="fw-semibold">{{ dept.department_name }}</div>
                      </td>
                      <td>{{ dept.total_employees }}</td>
                      <td>
                        <span class="badge bg-light text-dark">{{ dept.reviews_completed }}</span>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="progress flex-grow-1 me-2" style="height: 8px; width: 100px">
                            <div class="progress-bar bg-primary" :style="`width: ${dept.completion_rate || 0}%`"></div>
                          </div>
                          <span class="small text-muted">{{ Math.round(dept.completion_rate || 0) }}%</span>
                        </div>
                      </td>
                      <td>
                        <div v-if="dept.average_score" class="d-flex align-items-center">
                          <span class="badge" :class="getScoreBadge(dept.average_score)">{{ Math.round(dept.average_score) }}%</span>
                        </div>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-4">
                <vue-feather type="inbox" size="48" class="mb-2 opacity-50"></vue-feather>
                <p class="mb-0">No department data available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import notify from '@/utils/notify';
import performanceService from '@/api/performanceService';
import moment from 'moment';
import {useRoute, useRouter} from "vue-router";

export default {
  name: 'ReviewCycleDetails',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(false);
    const cycle = ref(null);
    const overview = ref({});
    const progress = ref({});
    const distribution = ref({});
    const departmentStats = ref([]);
    const topPerformers = ref([]);
    const needsAttention = ref([]);
    const trends = ref({});
    const fetchCycleAnalytics = async () => {
      loading.value = true;
      try {
        const res = await performanceService.getCycleAnalytics(route.params.id);
        const payload = res?.data || res || {};

        cycle.value = payload.cycle || null;
        overview.value = payload.overview || {};
        progress.value = payload.progress || {};
        distribution.value = payload.distribution || {};
        departmentStats.value = payload.departmentStats || [];
        topPerformers.value = payload.topPerformers || [];
        needsAttention.value = payload.needsAttention || [];
        trends.value = payload.trends || {};
      } catch (error) {
        notify.alert.error('Failed to load analytics data');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      if (!date) return 'N/A';
      return moment(date).format('MMM DD, YYYY');
    };

    const formatRating = (rating) => {
      const map = {
        exceptional: 'Exceptional',
        exceeds: 'Exceeds Expectations',
        meets: 'Meets Expectations',
        needs_improvement: 'Needs Improvement',
        unsatisfactory: 'Unsatisfactory'
      };
      return map[rating] || rating;
    };

    const getTrendIcon = () => {
      if (trends.value.trend_direction === 'up') return 'trending-up';
      if (trends.value.trend_direction === 'down') return 'trending-down';
      return 'minus';
    };

    const getTotalRatings = () => {
      return Object.values(distribution.value).reduce((sum, count) => sum + count, 0);
    };

    const getRatingColor = (rating) => {
      const colors = {
        exceptional: 'bg-success',
        exceeds: 'bg-primary',
        meets: 'bg-info',
        needs_improvement: 'bg-warning',
        unsatisfactory: 'bg-danger'
      };
      return colors[rating] || 'bg-secondary';
    };

    const getRatingBadge = (rating) => {
      const badges = {
        exceptional: 'bg-success',
        exceeds: 'bg-primary',
        meets: 'bg-info',
        needs_improvement: 'bg-warning',
        unsatisfactory: 'bg-danger'
      };
      return badges[rating] || 'bg-secondary';
    };

    const getPerformerBadge = (index) => {
      const badges = ['bg-warning', 'bg-info', 'bg-success'];
      return badges[index] || 'bg-secondary';
    };

    const getScoreBadge = (score) => {
      if (score >= 90) return 'bg-success';
      if (score >= 75) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    const goBack = () => {
      router.go(-1);
    };

    onMounted(() => {
      fetchCycleAnalytics();
    });

    return {
      loading,
      cycle,
      overview,
      progress,
      distribution,
      departmentStats,
      topPerformers,
      needsAttention,
      trends,
      formatDate,
      formatRating,
      getTrendIcon,
      getTotalRatings,
      getRatingColor,
      getRatingBadge,
      getPerformerBadge,
      getScoreBadge,
      goBack,
      fetchCycleAnalytics
    };
  }
};
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.08);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2c3e50;
}

.card {
  border-radius: 0.5rem;
}

.card-header {
  padding: 1rem 1.25rem;
}

.opacity-50 {
  opacity: 0.5;
}
</style>
